"use client"

import { useEffect, useRef, useSyncExternalStore } from 'react'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import mix from '@utils/styles/mix'
import Turnstile from 'src/pagesComponents/display/v2/Turnstile'

import offerStyles from 'src/pagesComponents/display/v2/Offer.module.css'
import styles from './Salon.module.css'

// Numer i e-mail wizytówki z API (nie ma ich w HTML): jeden stan na wizytówkę i rodzaj, wspólny dla nagłówka i paska.
const IDLE = Object.freeze({ challenge: false, message: '', siteKey: '', status: 'idle', value: null })
const states = new Map()
const listeners = new Set()
const get = (key) => states.get(key) || IDLE
const set = (key, next) => { states.set(key, { ...get(key), ...next }); listeners.forEach(l => l()) }
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l) }
const ERROR = { phone: 'Nie udało się pobrać numeru. Spróbuj ponownie albo napisz do firmy.', email: 'Nie udało się pobrać e-maila. Spróbuj ponownie.' }

async function reveal (id, kind, token) {
    const key = `${id}/${kind}`
    const current = get(key)
    if (current.status === 'loading' || current.status === 'ready') return
    set(key, { message: '', status: 'loading' })
    try {
        const res = await fetch(`/api/salon/${encodeURIComponent(id)}/${kind}`, {
            body: JSON.stringify(token ? { turnstile: token } : {}), cache: 'no-store', headers: { 'Content-Type': 'application/json' }, method: 'POST',
        })
        const json = await res.json().catch(() => ({}))
        const value = kind === 'phone' ? (Array.isArray(json.phones) && json.phones.length ? json.phones : null) : json.email || null
        if (res.status === 429) set(key, { challenge: Boolean(json.challenge), message: json.errors?.[0] || ERROR[kind], siteKey: json.siteKey || '', status: 'limited' })
        else if (!res.ok || !value) set(key, { message: ERROR[kind], status: 'error' })
        else set(key, { challenge: false, message: '', status: 'ready', value })
    } catch (err) {
        set(key, { message: ERROR[kind], status: 'error' })
    }
}

function useContact (id, kind) {
    const key = `${id}/${kind}`
    const snapshot = () => get(key)
    return useSyncExternalStore(subscribe, snapshot, snapshot)
}

const tel = (phone) => `tel:${String(phone).replace(/[^\d+]/g, '')}`

// Po kliknięciu (i tylko wtedy) fokus na pierwszej odsłoniętej wartości albo na wyzwaniu.
function useRevealFocus (state, ref) {
    const asked = useRef(false)
    useEffect(() => {
        if (asked.current && (state.status === 'ready' || state.status === 'limited')) {
            asked.current = false
            ref.current?.querySelector('a, iframe, [tabindex]')?.focus()
        }
    }, [state.status, ref])
    return asked
}

function Challenge ({ id, kind, state }) {
    return (
        <div className={styles.challenge}>
            <p className={mix([offerStyles.msg, offerStyles.msgWarn])} role="status">{state.message}</p>
            { state.challenge && state.siteKey && <Turnstile onToken={token => { set(`${id}/${kind}`, { status: 'idle' }); reveal(id, kind, token) }} siteKey={state.siteKey} /> }
        </div>
    )
}

// „Pokaż numer” / „Pokaż numery” w kolumnie kontaktu.
export function PhoneReveal ({ id }) {
    const state = useContact(id, 'phone')
    const box = useRef(null)
    const asked = useRevealFocus(state, box)
    return (
        <div className={styles.reveal} ref={box}>
            { state.status === 'ready'
                ? state.value.map(phone => <a className={styles.value} href={tel(phone)} key={phone}><SvgIcon name="phone" />{phone}</a>)
                : <button aria-busy={state.status === 'loading'} className={mix([offerStyles.btnPrimary, offerStyles.btnBlock, styles.phoneButton])} onClick={() => { asked.current = true; reveal(id, 'phone') }} type="button"><SvgIcon name="phone" />Pokaż numer</button> }
            { (state.status === 'limited' || state.status === 'error') && <Challenge id={id} kind="phone" state={state} /> }
        </div>
    )
}

// „Pokaż e-mail” — e-mail z ustawień wizytówki. variant: desk (kolumna kontaktu) albo mob (siatka przycisków).
export function EmailReveal ({ id, variant }) {
    const state = useContact(id, 'email')
    const box = useRef(null)
    const asked = useRevealFocus(state, box)
    return (
        <div className={mix([styles.reveal, variant === 'mob' ? styles.mob : styles.desk])} ref={box}>
            { state.status === 'ready'
                ? <a className={mix([styles.value, styles.valueMail])} href={`mailto:${state.value}`}><SvgIcon name="mail" />{state.value}</a>
                : <button aria-busy={state.status === 'loading'} className={mix([styles.btnLine, variant === 'desk' && offerStyles.btnBlock])} onClick={() => { asked.current = true; reveal(id, 'email') }} type="button"><SvgIcon name="mail" />Pokaż e-mail</button> }
            { (state.status === 'limited' || state.status === 'error') && <Challenge id={id} kind="email" state={state} /> }
        </div>
    )
}

// Pasek na telefonie (< 1024 px): pierwszy dotyk „Zadzwoń” pobiera numer i pokazuje go w przycisku (tel:), drugi dzwoni.
export function ContactBar ({ id, write }) {
    const state = useContact(id, 'phone')
    const ref = useRef(null)
    const asked = useRef(false)
    useEffect(() => {
        if (!asked.current) return
        if (state.status === 'ready') { asked.current = false; ref.current?.focus() }
        // limit: wyzwanie i komunikat w nagłówku (kolumna kontaktu widoczna, gdy jest wyzwanie)
        if (state.status === 'limited' || state.status === 'error') { asked.current = false; document.getElementById('kontakt')?.scrollIntoView({ block: 'center' }) }
    }, [state.status])
    return (
        <div aria-label="Kontakt z firmą" className={mix([offerStyles.bar, !write && offerStyles.barOne])} role="region">
            { state.status === 'ready'
                ? <a className={offerStyles.btnPrimary} href={tel(state.value[0])} ref={ref}><SvgIcon name="phone" />{state.value[0]}</a>
                : <button aria-busy={state.status === 'loading'} className={offerStyles.btnPrimary} onClick={() => { asked.current = true; reveal(id, 'phone') }} type="button"><SvgIcon name="phone" />Zadzwoń</button> }
            {write}
        </div>
    )
}
